import React, { useRef, useState } from "react";
import type { Price } from "@lemonadetraining/contracts";
import ProductCard from "@lemonadetraining/web-shared/components/ProductCard";

/* ================ Cover Art Pick And Preview ================ */
interface CoverArtPickAndPreviewProps {
  coverArtURL?: string | null;
  /** When true, the preview clears its locally-picked cover art back to none. */
  reset?: boolean;
  coachUID?: string;
  ownerUID?: string;
  coachName?: string;
  ownerName?: string;
  pricing?: Price | null;
  title?: string;
  effortLevel?: number | null;
  editable?: boolean;
  /** Called with the newly picked file. */
  onCoverArtChanged?: (file: File) => void;
}

/**
 * The product preview consumes a partial product shape rather than a full
 * `Product`: only the fields needed to render the cover-art preview are known
 * at pick time. The rest of `Product` is irrelevant here, so it is not modelled.
 */
interface ProductPreviewData {
  coverArtURL: string | null;
  ownerUID?: string;
  ownerName?: string;
  pricing?: Price | null;
  name: string;
  effortLevel: number | null;
  active: boolean;
}

const CoverArtPickAndPreview: React.FC<CoverArtPickAndPreviewProps> = (props) => {
  const [userCoverPicURL, setUserCoverPicURL] = useState<string | null>(props.coverArtURL ?? null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ----- Reset -------- */
  if (props.reset && userCoverPicURL !== null) {
    setUserCoverPicURL(null);
  }

  /* ------- [Handlers] --------- */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }
    const file = files[0];

    // No size gate: this component only picks and previews — the shared
    // resize pipeline shrinks the photo under the rule limit at upload time.
    setUserCoverPicURL(URL.createObjectURL(file));

    if (props.onCoverArtChanged) {
      props.onCoverArtChanged(file);
    }
  };

  /* Product Card Data */
  const productData: ProductPreviewData = {
    coverArtURL: userCoverPicURL,
    ownerUID: props.coachUID || props.ownerUID,
    ownerName: props.coachName || props.ownerName,
    pricing: props.pricing,
    name: props.title ? props.title : "",
    effortLevel: props.effortLevel ? props.effortLevel : null,
    active: true,
  };

  /* ------- [Return] ---------
   * The pick control is a real button (keyboard-operable, honestly named) and
   * exists only when editing is allowed — a disabled pick must never look like
   * a working one. The preview card is purely presentational here; wrapping it
   * in the picker's label would announce it as "View details" to screen
   * readers while actually opening a file dialog. */
  return (
    <div className="flex flex-col items-center gap-2">
      <ProductCard id="" data={productData} showAction={false} />
      {props.editable && (
        <>
          <input
            ref={fileInputRef}
            accept="image/*"
            id="cover-button-file"
            data-testid="cover-art-input"
            type="file"
            className="hidden"
            tabIndex={-1}
            onChange={handleFileInputChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Change cover art
          </button>
        </>
      )}
    </div>
  );
};

export default CoverArtPickAndPreview;
